 

import { useState, useEffect } from "react"
import Sidebar from "./Sidebar"
import Visualizer from "./Visualizer"
import Controls from "./Controls"
import EducationalContent from "./EducationalContent"
import "../styles/bm.css"

function Bm() {
  const [text, setText] = useState("")
  const [pattern, setPattern] = useState("")
  const [speed, setSpeed] = useState(1)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [algorithmSteps, setAlgorithmSteps] = useState([])
  const [showSidebar, setShowSidebar] = useState(false)
  const [activeSection, setActiveSection] = useState("overview")

  // Preprocess for bad character rule
  const preprocessBadChar = (pattern) => {
    const badChar = {}
    const m = pattern.length

    // Initialize all occurrences as -1
    for (let i = 0; i < m - 1; i++) {
      badChar[pattern[i]] = i
    }

    return badChar
  }

  // Preprocess for good suffix rule
  const preprocessGoodSuffix = (pattern) => {
    const m = pattern.length
    const shift = Array(m).fill(0)
    const borderPosition = Array(m + 1).fill(0)

    // Simple implementation for visualization purposes
    for (let i = 0; i < m; i++) {
      shift[i] = m
    }

    return shift
  }

  const runAlgorithm = () => {
    // Validate inputs
    if (!text || !pattern) {
      alert("Please enter both text and pattern before running the algorithm.")
      return
    }

    const steps = []
    const badChar = preprocessBadChar(pattern)
    const goodSuffix = preprocessGoodSuffix(pattern)

    let textPos = 0
    const n = text.length
    const m = pattern.length

    while (textPos <= n - m) {
      let patternPos = m - 1

      // Add initial position step
      steps.push({
        type: "position",
        textPos,
        patternPos: m - 1,
        description: `Aligning pattern at position ${textPos}`,
        explanation: `We start by aligning the pattern "${pattern}" at position ${textPos} in the text. The Boyer-Moore algorithm compares characters from right to left.`,
      })

      // Compare characters from right to left
      while (patternPos >= 0 && pattern[patternPos] === text[textPos + patternPos]) {
        steps.push({
          type: "compare",
          textPos: textPos + patternPos,
          patternPos,
          match: true,
          description: `Match: ${pattern[patternPos]} = ${text[textPos + patternPos]}`,
          explanation: `Character "${pattern[patternPos]}" at pattern position ${patternPos} matches with character "${text[textPos + patternPos]}" at text position ${textPos + patternPos}. We continue comparing from right to left.`,
        })
        patternPos--
      }

      // If we matched the entire pattern
      if (patternPos < 0) {
        steps.push({
          type: "match",
          textPos,
          description: `Pattern found at position ${textPos}`,
          explanation: `All characters in the pattern matched! We found the pattern "${pattern}" at position ${textPos} in the text. We now shift the pattern by its length (${m}) to look for more occurrences.`,
        })

        // Move to the next position
        textPos += m
      } else {
        // Mismatch occurred
        const mismatchChar = text[textPos + patternPos]
        steps.push({
          type: "compare",
          textPos: textPos + patternPos,
          patternPos,
          match: false,
          description: `Mismatch: ${pattern[patternPos]} ≠ ${mismatchChar}`,
          explanation: `Character "${pattern[patternPos]}" at pattern position ${patternPos} does not match with character "${mismatchChar}" at text position ${textPos + patternPos}. We need to shift the pattern.`,
        })

        // Calculate shift using bad character rule
        const badCharShift = patternPos - (badChar[mismatchChar] || -1)
        const badCharExplanation =
          badChar[mismatchChar] !== undefined
            ? `The mismatched character "${mismatchChar}" appears at position ${badChar[mismatchChar]} in the pattern.`
            : `The mismatched character "${mismatchChar}" does not appear in the pattern.`

        // Calculate shift using good suffix rule
        const goodSuffixShift = goodSuffix[patternPos]

        // Take the maximum of the two shifts
        const shift = Math.max(1, Math.max(badCharShift, goodSuffixShift))

        steps.push({
          type: "shift",
          textPos,
          shift,
          badCharShift,
          goodSuffixShift,
          description: `Shifting by ${shift} positions (Bad Char: ${badCharShift}, Good Suffix: ${goodSuffixShift})`,
          explanation: `Using the Boyer-Moore heuristics:\n\n1. Bad Character Rule: ${badCharExplanation} This gives us a shift of ${badCharShift}.\n\n2. Good Suffix Rule: The matched suffix gives us a shift of ${goodSuffixShift}.\n\nWe take the maximum of these shifts (${shift}) to ensure we don't miss any potential matches.`,
        })

        textPos += shift
      }
    }

    // Show sidebar with logs when algorithm runs
    setShowSidebar(true)
    setAlgorithmSteps(steps)
    setCurrentStep(0)
    setIsPlaying(true)
  }

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const handleReset = () => {
    setIsPlaying(false)
    setCurrentStep(0)
  }

  const handleStepForward = () => {
    if (currentStep < algorithmSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      setIsPlaying(false)
    }
  }

  const handleStepBackward = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleTextChange = (e) => {
    setText(e.target.value)
    handleReset()
  }

  const handlePatternChange = (e) => {
    setPattern(e.target.value)
    handleReset()
  }

  const handleSpeedChange = (e) => {
    setSpeed(Number.parseFloat(e.target.value))
  }

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar)
  }

  return (
    <div className={`bmapp-container ${showSidebar ? "with-bmsidebar" : ""}`}>
      {/* {showSidebar && (
        <Sidebar
          currentStep={currentStep}
          algorithmSteps={algorithmSteps}
          isPlaying={isPlaying}
          onClose={() => setShowSidebar(false)}
        />
      )} */}
      <div className="bmmain-content">
        <h1>Boyer-Moore Algorithm Visualizer</h1>

        <EducationalContent activeSection={activeSection} setActiveSection={setActiveSection} />

        <Controls
          text={text}
          pattern={pattern}
          speed={speed}
          isPlaying={isPlaying}
          onTextChange={handleTextChange}
          onPatternChange={handlePatternChange}
          onSpeedChange={handleSpeedChange}
          onPlayPause={handlePlayPause}
          onStepForward={handleStepForward}
          onStepBackward={handleStepBackward}
          onReset={handleReset}
          onRun={runAlgorithm}
          showLogs={showSidebar}
          // toggleLogs={toggleSidebar}
        />

        <Visualizer
          text={text}
          pattern={pattern}
          currentStep={currentStep < algorithmSteps.length ? algorithmSteps[currentStep] : null}
          isPlaying={isPlaying}
          speed={speed}
          onStepComplete={handleStepForward}
        />
      </div>
    </div>
  )
}

export default Bm

